import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, formatApiError, trackClick } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { ACA_COMPARE_QUIZ, scoreQuiz } from "@/lib/indemnityQuiz";

// Steps in the conversation
const STEPS = {
  GREET: "greet",
  ZIP: "zip",
  SITUATION: "situation",
  QUIZ_OFFER: "quiz_offer",
  QUIZ: "quiz",
  QUIZ_RESULT: "quiz_result",
  DENTAL: "dental",
  CONTACT: "contact",
  DONE: "done",
};

const SITUATIONS = [
  { value: "Turning 65", emoji: "🎉" },
  { value: "Switching coverage", emoji: "🔄" },
  { value: "Just exploring", emoji: "🔎" },
];

const Bubble = ({ role, children }) => {
  const isBot = role === "bot";
  return (
    <div
      className={`flex ${isBot ? "justify-start" : "justify-end"} animate-[fadeIn_0.25s_ease]`}
      data-testid={`chat-bubble-${role}`}
    >
      <div
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isBot
            ? "bg-slate-100 text-[var(--ink)] rounded-bl-md"
            : "bg-[var(--brand-primary)] text-white rounded-br-md"
        }`}
      >
        {children}
      </div>
    </div>
  );
};

const QuizResultCard = ({ result }) => (
  <div data-testid="chat-quiz-result" data-result={result.id}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Your best fit</p>
    <p className="mt-1 font-display text-base text-[var(--ink)]">{result.title}</p>
    <p className="mt-1 text-xs text-slate-600 leading-relaxed">{result.summary}</p>
    <div className="mt-3 flex flex-col gap-1.5">
      {result.ctaUrl && (
        <a
          href={result.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="chat-quiz-result-cta"
          onClick={() => trackClick(result.trackId, `${result.title} (chat quiz)`, window.location.pathname)}
          className="inline-flex items-center justify-center rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] hover:text-white text-xs font-semibold px-4 py-2 transition-colors"
        >
          {result.ctaLabel} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </a>
      )}
      <Link
        to={result.pagePath}
        data-testid="chat-quiz-result-page"
        className="inline-flex items-center justify-center rounded-full border border-slate-300 hover:border-[var(--brand-primary)] text-[var(--ink)] text-xs font-medium px-4 py-2 transition-colors"
      >
        {result.ctaUrl ? "Compare both on the ACA page" : result.ctaLabel}
      </Link>
    </div>
    <p className="mt-2 text-[10px] text-slate-400">Optional supplemental coverage — not a substitute for a Marketplace plan.</p>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEPS.GREET);
  const [history, setHistory] = useState([
    {
      role: "bot",
      text: "Hi there! 👋 I'm Christian's virtual assistant at 30A Health Insurance. Mind if I ask a few quick questions to point you to the right coverage?",
    },
  ]);
  const [zip, setZip] = useState("");
  const [situation, setSituation] = useState(null);
  const [wantsDental, setWantsDental] = useState(null);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll to newest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, step, open]);

  const pushBot = (text) => setHistory((h) => [...h, { role: "bot", text }]);
  const pushUser = (text) => setHistory((h) => [...h, { role: "user", text }]);

  const startConversation = () => {
    pushUser("Sure, ask away.");
    setTimeout(() => {
      pushBot("Great! First — what's your ZIP code? Plans and pricing vary by county.");
      setStep(STEPS.ZIP);
    }, 350);
  };

  const declineConversation = () => {
    pushUser("Maybe later.");
    setTimeout(() => {
      pushBot("No worries — feel free to call 850-687-7606 anytime, or come back when you're ready. 🌊");
      setStep(STEPS.DONE);
    }, 350);
  };

  const submitZip = () => {
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) {
      toast.error("Please enter a valid 5-digit ZIP.");
      return;
    }
    pushUser(z);
    setTimeout(() => {
      pushBot(
        "Thanks! Quick one — are you turning 65 soon, switching your current coverage, or just exploring?"
      );
      setStep(STEPS.SITUATION);
    }, 300);
  };

  const askDental = () => {
    pushBot(
      "One more — would you like to add a dental quote? Adding standalone dental is one of the easiest ways to protect your savings from surprise bills."
    );
    setStep(STEPS.DENTAL);
  };

  const pickSituation = (value) => {
    setSituation(value);
    pushUser(value);
    setTimeout(() => {
      pushBot(
        "Got it. Want a 30-second quiz? Three quick questions tell you whether a hospital-only indemnity plan or a life + cancer + critical illness bundle fits you better."
      );
      setStep(STEPS.QUIZ_OFFER);
    }, 300);
  };

  const startQuiz = () => {
    pushUser("Sure, let's do the quiz.");
    setQuizIndex(0);
    setQuizAnswers([]);
    setTimeout(() => {
      pushBot(`1 of ${ACA_COMPARE_QUIZ.length}: ${ACA_COMPARE_QUIZ[0].question}`);
      setStep(STEPS.QUIZ);
    }, 300);
  };

  const skipQuiz = () => {
    pushUser("Skip the quiz.");
    setTimeout(askDental, 300);
  };

  const answerQuiz = (answer) => {
    pushUser(answer.label);
    const next = [...quizAnswers, answer.scoreFor];
    setQuizAnswers(next);
    const nextIndex = quizIndex + 1;
    setTimeout(() => {
      if (nextIndex < ACA_COMPARE_QUIZ.length) {
        setQuizIndex(nextIndex);
        pushBot(`${nextIndex + 1} of ${ACA_COMPARE_QUIZ.length}: ${ACA_COMPARE_QUIZ[nextIndex].question}`);
      } else {
        const result = scoreQuiz(next);
        setQuizResult(result);
        setHistory((h) => [...h, { role: "bot", type: "quiz_result", result }]);
        setStep(STEPS.QUIZ_RESULT);
      }
    }, 300);
  };

  const continueAfterQuiz = () => {
    pushUser("Got it, thanks.");
    setTimeout(askDental, 300);
  };

  const pickDental = (val) => {
    setWantsDental(val);
    pushUser(val ? "Yes, please." : "Not right now.");
    setTimeout(() => {
      pushBot(
        "Awesome. Last step — how can Christian reach you? He'll personally follow up within one business day, no spam."
      );
      setStep(STEPS.CONTACT);
    }, 300);
  };

  const submitContact = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !phone.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    setSubmitting(true);
    pushUser(`${name.trim()} · ${phone.trim()}${email.trim() ? ` · ${email.trim()}` : ""}`);
    try {
      await api.post("/chat/lead", {
        zip_code: zip.trim(),
        situation,
        wants_dental: !!wantsDental,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        quiz_result: quizResult ? `${quizResult.title} (chat quiz)` : undefined,
      });
      setTimeout(() => {
        pushBot(
          `Thanks, ${name.trim().split(" ")[0]}! 🎉 Christian will reach out within one business day. In the meantime, you can browse our plan pages or call 850-687-7606.`
        );
        setStep(STEPS.DONE);
        setSubmitting(false);
      }, 400);
    } catch (err) {
      setSubmitting(false);
      toast.error(formatApiError(err));
    }
  };

  const resetChat = () => {
    setStep(STEPS.GREET);
    setHistory([
      {
        role: "bot",
        text: "Hi there! 👋 I'm Christian's virtual assistant at 30A Health Insurance. Mind if I ask a few quick questions to point you to the right coverage?",
      },
    ]);
    setZip("");
    setSituation(null);
    setWantsDental(null);
    setQuizIndex(0);
    setQuizAnswers([]);
    setQuizResult(null);
    setName("");
    setPhone("");
    setEmail("");
  };

  return (
    <>
      {/* Floating launcher button */}
      {!open && (
        <button
          type="button"
          aria-label="Open chat"
          data-testid="chat-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 sm:bottom-24 sm:right-7 z-[60] inline-flex items-center gap-2 rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white shadow-lg shadow-slate-900/15 pl-4 pr-5 py-3 transition-all duration-200 hover:scale-[1.02]"
        >
          <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15">
            <MessageCircle className="w-4 h-4" />
            <span className="absolute top-0 right-0 w-2 h-2 rounded-full bg-[var(--brand-secondary)] ring-2 ring-[var(--brand-primary)] animate-pulse" />
          </span>
          <span className="text-sm font-medium whitespace-nowrap">Chat with us</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-20 right-5 sm:bottom-24 sm:right-7 z-[60] w-[min(94vw,380px)] h-[min(75vh,560px)] flex flex-col bg-white rounded-2xl border border-slate-200 shadow-2xl shadow-slate-900/20 overflow-hidden animate-[slideUp_0.25s_ease]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[var(--ink)] text-white">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white p-0.5">
                <Logo className="h-full w-auto" />
              </span>
              <div>
                <p className="text-sm font-semibold leading-none">30A Health Assistant</p>
                <p className="text-[10px] text-slate-300 mt-0.5 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                  Usually replies in minutes
                </p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close chat"
              data-testid="chat-close"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-200 hover:bg-white/10"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[var(--bg-primary)]"
            data-testid="chat-messages"
          >
            {history.map((m, i) => (
              <Bubble key={i} role={m.role}>
                {m.type === "quiz_result" ? <QuizResultCard result={m.result} /> : m.text}
              </Bubble>
            ))}
            {submitting && (
              <Bubble role="bot">
                <span className="inline-flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…
                </span>
              </Bubble>
            )}
          </div>

          {/* Step controls */}
          <div className="border-t border-slate-200 p-3 bg-white">
            {step === STEPS.GREET && (
              <div className="flex gap-2" data-testid="chat-step-greet">
                <button
                  type="button"
                  data-testid="chat-start"
                  onClick={startConversation}
                  className="flex-1 rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium py-2.5 transition-colors"
                >
                  Sure, ask away
                </button>
                <button
                  type="button"
                  data-testid="chat-decline"
                  onClick={declineConversation}
                  className="rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium px-4 py-2.5 transition-colors"
                >
                  Maybe later
                </button>
              </div>
            )}

            {step === STEPS.ZIP && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitZip();
                }}
                className="flex gap-2"
                data-testid="chat-step-zip"
              >
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                  placeholder="32459"
                  autoFocus
                  data-testid="chat-input-zip"
                  className="flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm tracking-widest text-center outline-none focus:border-[var(--brand-primary)] focus:bg-white"
                />
                <button
                  type="submit"
                  data-testid="chat-submit-zip"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}

            {step === STEPS.SITUATION && (
              <div className="grid grid-cols-1 gap-2" data-testid="chat-step-situation">
                {SITUATIONS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    data-testid={`chat-situation-${s.value.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => pickSituation(s.value)}
                    className="text-left rounded-xl border border-slate-200 hover:border-[var(--brand-primary)] hover:bg-slate-50 px-4 py-2.5 text-sm transition-colors"
                  >
                    <span className="mr-2">{s.emoji}</span> {s.value}
                  </button>
                ))}
              </div>
            )}

            {step === STEPS.QUIZ_OFFER && (
              <div className="grid grid-cols-2 gap-2" data-testid="chat-step-quiz-offer">
                <button
                  type="button"
                  data-testid="chat-quiz-start"
                  onClick={startQuiz}
                  className="rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] hover:text-white text-sm font-semibold py-2.5 transition-colors"
                >
                  Take the quiz
                </button>
                <button
                  type="button"
                  data-testid="chat-quiz-skip"
                  onClick={skipQuiz}
                  className="rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium py-2.5 transition-colors"
                >
                  Skip for now
                </button>
              </div>
            )}

            {step === STEPS.QUIZ && (
              <div className="grid grid-cols-1 gap-2" data-testid="chat-step-quiz">
                {ACA_COMPARE_QUIZ[quizIndex].answers.map((a, i) => (
                  <button
                    key={a.label}
                    type="button"
                    data-testid={`chat-quiz-q${quizIndex}-a${i}`}
                    onClick={() => answerQuiz(a)}
                    className="text-left rounded-xl border border-slate-200 hover:border-[var(--brand-primary)] hover:bg-slate-50 px-4 py-2.5 text-sm transition-colors"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}

            {step === STEPS.QUIZ_RESULT && (
              <div className="flex flex-col gap-2" data-testid="chat-step-quiz-result">
                <button
                  type="button"
                  data-testid="chat-quiz-continue"
                  onClick={continueAfterQuiz}
                  className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium py-2.5 transition-colors"
                >
                  Continue <ArrowRight className="inline w-4 h-4 ml-1" />
                </button>
              </div>
            )}

            {step === STEPS.DENTAL && (
              <div className="grid grid-cols-2 gap-2" data-testid="chat-step-dental">
                <button
                  type="button"
                  data-testid="chat-dental-yes"
                  onClick={() => pickDental(true)}
                  className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium py-2.5 transition-colors"
                >
                  Yes, please
                </button>
                <button
                  type="button"
                  data-testid="chat-dental-no"
                  onClick={() => pickDental(false)}
                  className="rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium py-2.5 transition-colors"
                >
                  Not right now
                </button>
              </div>
            )}

            {step === STEPS.CONTACT && (
              <form
                onSubmit={submitContact}
                className="space-y-2"
                data-testid="chat-step-contact"
              >
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="chat-input-name"
                  className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm outline-none focus:border-[var(--brand-primary)] focus:bg-white"
                />
                <input
                  type="tel"
                  placeholder="Phone (best way to reach you)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="chat-input-phone"
                  className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm outline-none focus:border-[var(--brand-primary)] focus:bg-white"
                />
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="chat-input-email"
                  className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm outline-none focus:border-[var(--brand-primary)] focus:bg-white"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="chat-submit-contact"
                  className="w-full inline-flex items-center justify-center rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium py-2.5 transition-colors disabled:opacity-60"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Send to Christian <ArrowRight className="w-4 h-4 ml-1.5" />
                    </>
                  )}
                </button>
                <p className="text-[10px] text-slate-400 text-center">
                  By submitting you consent to be contacted about insurance options.
                </p>
              </form>
            )}

            {step === STEPS.DONE && (
              <div className="flex flex-col gap-2" data-testid="chat-step-done">
                <button
                  type="button"
                  data-testid="chat-restart"
                  onClick={resetChat}
                  className="rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium py-2.5 transition-colors"
                >
                  Start a new chat
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Keyframes for in-bubble fade and panel slide-up */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};
